import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Card, Container } from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';
import ModalImagePreview from 'components/Modals/ModalImagePreview';

import {
  LOCATIONS,
  TCXH_FEEDBACK_STATUS,
  TCXH_FEEDBACK_STATUS_LIST,
  themeColor,
} from 'constants/index';

import ModalResolveComplaint from 'pages/ManagerComplaint/ComplaintDetail/components/ModalResolveComplaint';
import ModalRejectComplaint from 'pages/ManagerComplaint/ComplaintDetail/components/ModalRejectComplaint';
import {
  actionGetSocialOrganizationFeedbackDetail,
  actionUpdateSocialOrganizationFeedback,
  // actionResolveSocialOrganizationFeedback,
  // actionRejectSocialOrganizationFeedback,
  actionClearSocialOrganizationFeedbackDetail,
} from 'store/manager-tcxh-feedback/action';
import styles from './TcxhFeedbackDetail.module.scss';

const getStatusBadgeColor = (status) => {
  switch (status) {
    case TCXH_FEEDBACK_STATUS.APPROVED:
    case TCXH_FEEDBACK_STATUS.RESOLVED:
    case 'APPROVED':
    case 'RESOLVED':
      return 'success';
    case TCXH_FEEDBACK_STATUS.PENDING:
    case 'PENDING':
      return 'caution';
    case TCXH_FEEDBACK_STATUS.REJECTED:
    case 'REJECTED':
      return 'error';
    default:
      return 'info';
  }
};

const getStatusLabel = (status) => {
  const found = TCXH_FEEDBACK_STATUS_LIST.find((item) => item.value === status);
  return found ? found.label : status || '-';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

function TcxhFeedbackDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    socialOrganizationFeedbackDetail: feedback,
    isLoadingGetSocialOrganizationFeedbackDetail: isLoading,
    isLoadingUpdateSocialOrganizationFeedback: isLoadingUpdate,
    isLoadingResolveSocialOrganizationFeedback: isLoadingResolve,
    isLoadingRejectSocialOrganizationFeedback: isLoadingReject,
  } = useSelector((state) => state.ManagerTcxhFeedback || {});

  const [preview, setPreview] = useState({
    isOpen: false,
    images: [],
    initialIndex: 0,
  });

  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(actionGetSocialOrganizationFeedbackDetail(id));
    }
    return () => {
      dispatch(actionClearSocialOrganizationFeedbackDetail());
    };
  }, [id, dispatch]);

  const displayId = feedback?.feedbackId || feedback?._id || id;
  const isPending = feedback?.status === TCXH_FEEDBACK_STATUS.PENDING;

  const breadcrumbList = useMemo(() => [
    {
      name: 'Quản lý khiếu nại',
      isActive: false,
      link: LOCATIONS.MANAGER_COMPLAINT,
    },
    {
      name: 'Tiếp nhận phản ánh của TCXH',
      isActive: false,
      link: LOCATIONS.MANAGER_TCXH_FEEDBACK,
    },
    {
      name: 'Chi tiết phản ánh',
      isActive: true,
      link: '',
    },
  ], [displayId]);

  const handleConfirmResolve = (values) => {
    dispatch(
      actionUpdateSocialOrganizationFeedback({
        id: feedback?._id || id,
        status: 'APPROVED',
        response: values?.response || values?.note || '',
        callback: () => setIsResolveModalOpen(false),
      }),
    );
  };

  const handleConfirmReject = (values) => {
    dispatch(
      actionUpdateSocialOrganizationFeedback({
        id: feedback?._id || id,
        status: 'REJECTED',
        response: values?.response || values?.note || '',
        callback: () => setIsRejectModalOpen(false),
      }),
    );
  };

  return (
    <div className={`page-content ${styles.Page}`}>
      <Loading loading={isLoading} loaderOnPage />
      <Meta name={`Chi tiết phản ánh TCXH #${displayId}`} />

      <Container fluid>
        <Breadcrumb
          pageTitle="Tiếp nhận phản ánh của TCXH"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <div className="DetailHeader">
            <div>
              <h4 className="DetailHeader__Title">
                {`Phản ánh TCXH: #${displayId}`}
              </h4>
            </div>

            {isPending && (
              <div className="DetailHeader__Actions">
                <Button
                  color={themeColor.DANGER}
                  onClick={() => setIsRejectModalOpen(true)}
                >
                  Từ chối
                </Button>
                <Button
                  color={themeColor.SUCCESS}
                  onClick={() => setIsResolveModalOpen(true)}
                >
                  Tiếp nhận
                </Button>
              </div>
            )}
          </div>

          <div className="InfoGrid">
            {/* 1. Tên tổ chức xã hội */}
            <div className="InfoItem">
              <span className="InfoItem__Label">Tên tổ chức xã hội</span>
              <span className="InfoItem__Value">
                {feedback?.organizationName || feedback?.organization || '-'}
              </span>
            </div>

            {/* 2. Số quyết định thành lập */}
            <div className="InfoItem">
              <span className="InfoItem__Label">Số quyết định thành lập</span>
              <span className="InfoItem__Value">
                {feedback?.decisionNumber || feedback?.establishmentDecisionNumber || '-'}
              </span>
            </div>

            {/* 3. Địa chỉ email */}
            <div className="InfoItem">
              <span className="InfoItem__Label">Địa chỉ email</span>
              <span className="InfoItem__Value">{feedback?.email || '-'}</span>
            </div>

            {/* 4. Ngày gửi */}
            <div className="InfoItem">
              <span className="InfoItem__Label">Ngày gửi</span>
              <span className="InfoItem__Value">
                {formatDate(feedback?.createdAt)}
              </span>
            </div>

            {/* 5. Trạng thái */}
            <div className="InfoItem">
              <span className="InfoItem__Label">Trạng thái</span>
              <span
                className={`StatusBadge StatusBadge--${getStatusBadgeColor(feedback?.status)}`}
              >
                <span className="StatusBadgeDot" />
                {getStatusLabel(feedback?.status)}
              </span>
            </div>

            {/* 6. Tài khoản gửi phản ánh (nếu có) */}
            {feedback?.user && (
              <div className="InfoItem">
                <span className="InfoItem__Label">Tài khoản người gửi</span>
                <span className="InfoItem__Value">
                  {feedback.user.fullName || '-'}
                  {feedback.user.phoneNumber ? ` (${feedback.user.phoneNumber})` : ''}
                </span>
              </div>
            )}
          </div>

          {/* Nội dung phản ánh */}
          <div className="ContentBlock">
            <h5 className="ContentBlock__Title">Nội dung liên hệ & Phản ánh</h5>
            <p className="ContentBlock__Text">{feedback?.content || '-'}</p>
          </div>

          {/* Ảnh đính kèm */}
          {feedback?.images && feedback.images.length > 0 && (
            <div className="ContentBlock">
              <h5 className="ContentBlock__Title">Ảnh / Tài liệu đính kèm</h5>
              <div className="ImageGallery">
                {feedback.images.map((imgUrl, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="GalleryThumb"
                    onClick={() => {
                      setPreview({
                        isOpen: true,
                        images: feedback.images,
                        initialIndex: index,
                      });
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setPreview({
                          isOpen: true,
                          images: feedback.images,
                          initialIndex: index,
                        });
                      }
                    }}
                  >
                    <img src={imgUrl} alt={`Ảnh đính kèm ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phản hồi & Kết quả xử lý */}
          {feedback?.status && feedback.status !== TCXH_FEEDBACK_STATUS.PENDING && (
            <div className="ResponseCard">
              <div className="ResponseCard__Header">
                <span>
                  {feedback.status === TCXH_FEEDBACK_STATUS.APPROVED
                  || feedback.status === TCXH_FEEDBACK_STATUS.RESOLVED
                  || feedback.status === 'APPROVED'
                  || feedback.status === 'RESOLVED'
                    ? 'Kết quả xử lý & Phản hồi'
                    : 'Lý do từ chối'}
                </span>
                {feedback.resolvedAt && (
                  <span className="ResponseCard__Date">
                    {`Xử lý lúc: ${formatDate(feedback.resolvedAt)}`}
                  </span>
                )}
              </div>
              <div className="ResponseCard__Content">
                {feedback.response || feedback.note || 'Chưa có thông tin phản hồi'}
              </div>

              {feedback.responseImages && feedback.responseImages.length > 0 && (
                <div className="ImageGallery" style={{ marginTop: '14px' }}>
                  {feedback.responseImages.map((imgUrl, index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className="GalleryThumb"
                      onClick={() => {
                        setPreview({
                          isOpen: true,
                          images: feedback.responseImages,
                          initialIndex: index,
                        });
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setPreview({
                            isOpen: true,
                            images: feedback.responseImages,
                            initialIndex: index,
                          });
                        }
                      }}
                    >
                      <img src={imgUrl} alt={`Minh chứng ${index + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Modal Duyệt / Tiếp nhận khiếu nại - dùng chung component */}
        <ModalResolveComplaint
          isOpen={isResolveModalOpen}
          isLoading={isLoadingUpdate || isLoadingResolve}
          complaintId={displayId}
          showUploadImages={false}
          onClose={() => setIsResolveModalOpen(false)}
          onConfirm={handleConfirmResolve}
        />

        {/* Modal Từ chối khiếu nại - dùng chung component */}
        <ModalRejectComplaint
          isOpen={isRejectModalOpen}
          isLoading={isLoadingUpdate || isLoadingReject}
          complaintId={displayId}
          showUploadImages={false}
          onClose={() => setIsRejectModalOpen(false)}
          onConfirm={handleConfirmReject}
        />

        {/* Modal Image Preview */}
        <ModalImagePreview
          isOpen={preview.isOpen}
          images={preview.images}
          initialIndex={preview.initialIndex}
          onClose={() => setPreview((prev) => ({ ...prev, isOpen: false }))}
        />
      </Container>
    </div>
  );
}

export default TcxhFeedbackDetail;
